import { FC, useState } from 'react';
import { MdClose } from 'react-icons/md';
import { Button, Modal, Radio } from 'components/core';
import { useAddresses } from 'reactQuery/queries';
import { useI18n } from 'contexts/i18n';
import { Styled } from './';
import { ChangeSubscriptionAddressModalProps } from './ChangeSubscriptionAddressModal.types';
import {
  getMailingNameFromAddress,
  getNameFromAddress,
} from '../../OrderAddress';
import { LoadingSpinner, LoadingWrapper } from 'styles/sharedStyles';
import { useUpdateSubscriptionAddress } from 'reactQuery/mutations';

const ChangeSubscriptionAddressModal: FC<
  ChangeSubscriptionAddressModalProps
> = ({
  onClose,
  subscriptionId,
  subscriptionBillingAddressId,
  subscriptionShippingAddressId,
}) => {
  const i18n = useI18n();
  const {
    data: address,
    isLoading: isAddressLoading,
    isFetching: isAddressFetching,
    isSuccess: isAddressSuccess,
  } = useAddresses();

  const [billAddressId, setBillAddressId] = useState(
    subscriptionBillingAddressId
  );

  const [shipAddressId, setShipAddressId] = useState(
    subscriptionShippingAddressId
  );

  const updateSubscriptionAddressMutation = useUpdateSubscriptionAddress();

  const handleChangeAddress = async () => {
    await updateSubscriptionAddressMutation.mutateAsync({
      subscriptionId: subscriptionId,
      billAddressId,
      shipAddressId,
    });
    onClose();
  };

  return (
    <Modal overlaysize="medium">
      <Styled.Container>
        <Styled.ModalHeader>
          <Styled.Title>{i18n['Change Subscription Address:']}</Styled.Title>
          <Styled.CloseButton onClick={onClose}>
            <MdClose size={24} />
          </Styled.CloseButton>
        </Styled.ModalHeader>

        <Styled.ModalBody>
          {(isAddressLoading || isAddressFetching) && (
            <LoadingWrapper>
              <LoadingSpinner width="25px" height="25px" isPrimary={true} />
            </LoadingWrapper>
          )}
          {isAddressSuccess && (
            <>
              <Styled.ShippingAddress>
                <Styled.SubTitle>
                  {i18n['Select Shipping Address']}
                </Styled.SubTitle>
                <div>
                  {address?.shippingAddresses.map(shippingAddress => {
                    return (
                      <Styled.AddressContainer
                        key={shippingAddress.id}
                        isSelected={
                          parseInt(shippingAddress.id) === shipAddressId
                        }
                        onClick={() =>
                          setShipAddressId(parseInt(shippingAddress.id))
                        }
                      >
                        <Radio
                          checked={
                            parseInt(shippingAddress.id) === shipAddressId
                          }
                          onChange={() =>
                            setShipAddressId(parseInt(shippingAddress.id))
                          }
                          disabled={false}
                        />
                        <Styled.AddressWrapper>
                          {getNameFromAddress(shippingAddress).length > 0 ? (
                            <Styled.MailingName>
                              {getNameFromAddress(shippingAddress)}
                            </Styled.MailingName>
                          ) : (
                            ''
                          )}

                          <Styled.MailingName>
                            {getMailingNameFromAddress(shippingAddress)}
                          </Styled.MailingName>
                          <Styled.Address>
                            {shippingAddress.address1}{' '}
                            {shippingAddress.address2}{' '}
                            {shippingAddress.address3}
                            <br />
                            {shippingAddress.city}, {shippingAddress.stateName}{' '}
                            {shippingAddress.zipcode},{' '}
                            {shippingAddress.countryIso}
                          </Styled.Address>
                        </Styled.AddressWrapper>
                      </Styled.AddressContainer>
                    );
                  })}
                </div>
              </Styled.ShippingAddress>

              <Styled.BillingAddress>
                <Styled.SubTitle>
                  {i18n['Select Billing Address']}
                </Styled.SubTitle>

                {
                  <div>
                    {address?.billingAddresses.map(billingAddress => {
                      return (
                        <Styled.AddressContainer
                          key={billingAddress.id}
                          isSelected={
                            parseInt(billingAddress.id) === billAddressId
                          }
                          onClick={() =>
                            setBillAddressId(parseInt(billingAddress.id))
                          }
                        >
                          <Radio
                            checked={
                              parseInt(billingAddress.id) === billAddressId
                            }
                            onChange={() =>
                              setBillAddressId(parseInt(billingAddress.id))
                            }
                            disabled={false}
                          />
                          <Styled.AddressWrapper>
                            {getNameFromAddress(billingAddress).length > 0 ? (
                              <Styled.MailingName>
                                {getNameFromAddress(billingAddress)}
                              </Styled.MailingName>
                            ) : (
                              ''
                            )}
                            <Styled.MailingName>
                              {getMailingNameFromAddress(billingAddress)}
                            </Styled.MailingName>
                            <Styled.Address>
                              {billingAddress.address1}{' '}
                              {billingAddress.address2}{' '}
                              {billingAddress.address3}
                              <br />
                              {billingAddress.city}, {billingAddress.stateName}{' '}
                              {billingAddress.zipcode},{' '}
                              {billingAddress.countryIso}
                            </Styled.Address>
                          </Styled.AddressWrapper>
                        </Styled.AddressContainer>
                      );
                    })}
                  </div>
                }
              </Styled.BillingAddress>
            </>
          )}
        </Styled.ModalBody>

        <Styled.ModalFooter>
          <Styled.ButtonWrapper>
            <Button size="md" variant="secondary" onClick={onClose}>
              {i18n['Cancel']}
            </Button>
            <Button
              size="md"
              onClick={() => handleChangeAddress()}
              disabled={
                isAddressLoading ||
                isAddressFetching ||
                updateSubscriptionAddressMutation.isLoading ||
                !billAddressId ||
                !shipAddressId
              }
              loading={updateSubscriptionAddressMutation.isLoading}
            >
              {i18n['Confirm']}
            </Button>
          </Styled.ButtonWrapper>
        </Styled.ModalFooter>
      </Styled.Container>
    </Modal>
  );
};

export default ChangeSubscriptionAddressModal;
